import React, { useMemo, useState } from 'react';
import { DataTable, useDataTable } from '@onward-delivery/design';
import { carrierColumns } from './columns';
import { filterCarrierRows } from './utils';
import ExpandedCarrier from './ExpandedCarrier';

// Overrides the library's own classes, which is safe because it merges className with tailwind-merge.
// Child selectors only, so nothing reaches a table inside an expanded row.
const TABLE_CLASS = [
    'rounded-xl border-slate-200',
    '[&>div>table>thead]:bg-slate-50',
    '[&>div>table>thead>tr>th]:after:bg-slate-200',
    '[&>div>table>thead>tr>th]:px-2 [&>div>table>thead>tr>th]:py-4',
    '[&>div>table>tbody]:border-slate-200 [&>div>table>tbody>tr]:border-slate-200',
    '[&>div>table>thead>tr>th:first-child]:pl-5 [&>div>table>thead>tr>th:last-child]:pr-5',
    '[&>div>table>thead>tr>th:first-child]:text-left',
    '[&>div>table>thead>tr>th>div>div>div]:justify-center [&>div>table>thead>tr>th:first-child>div>div>div]:justify-start',
    // Body cells and the pinned copy of an open row, which is its own table laid over this one, share one rule set.
    '[:is(&>div,&>[data-slot=datatable-pinned-row]>div)>table>tbody>tr>td]:px-2 [:is(&>div,&>[data-slot=datatable-pinned-row]>div)>table>tbody>tr>td]:py-3.5 [:is(&>div,&>[data-slot=datatable-pinned-row]>div)>table>tbody>tr>td]:text-center',
    '[:is(&>div,&>[data-slot=datatable-pinned-row]>div)>table>tbody>tr>td:first-child]:pl-5 [:is(&>div,&>[data-slot=datatable-pinned-row]>div)>table>tbody>tr>td:first-child]:text-left',
    '[:is(&>div,&>[data-slot=datatable-pinned-row]>div)>table>tbody>tr>td:last-child]:pr-5',
    '[&>[data-slot=datatable-pinned-row]>div]:border-slate-200',
    // The library's idle sort icon is the one at opacity-50; the arrow still shows once sorted.
    '[&>div>table>thead_svg.opacity-50]:hidden',
].join(' ');

// renderSubTable adds the library's own chevron column; the toggle lives in the actions column instead.
const HIDDEN_EXPANDER_COLUMN = { expander: false };

const Carriers = ({
    rows = [],
    search = '',
    shipperIds = [],
    loading = false,
    error = null,
    shipperGroupsByCarrier = {},
    marketplaceShippers = [],
    flexCentersByContractor = {},
    contractorsByFlexCenter,
    defaultPricingByCarrier = {},
    defaultIntegrationsByCarrier = {},
    onToggleActive,
    onOpen,
    onDetails,
}) => {
    const [expanded, setExpanded] = useState({});

    const columns = useMemo(
        () => carrierColumns({ onToggleActive, onOpen, onDetails, rows }),
        [onToggleActive, onOpen, onDetails, rows]
    );
    const visibleRows = useMemo(
        () =>
            filterCarrierRows(rows, {
                search,
                shipperIds,
                contractorsByFlexCenter,
                shipperGroupsByCarrier,
                marketplaceShippers,
            }),
        [rows, search, shipperIds, contractorsByFlexCenter, shipperGroupsByCarrier, marketplaceShippers]
    );

    const table = useDataTable({
        columns,
        data: visibleRows,
        getRowId: (row) => row.client_id,
        expanded,
        onExpandedChange: setExpanded,
        columnVisibility: HIDDEN_EXPANDER_COLUMN,
        renderSubTable: (row) => (
            <ExpandedCarrier
                row={row}
                shipperGroups={shipperGroupsByCarrier[row.client_id]}
                marketplaceShippers={marketplaceShippers}
                flexCenters={flexCentersByContractor[row.client_id]}
                contractors={contractorsByFlexCenter?.[row.client_id]}
                defaultPricing={defaultPricingByCarrier[row.client_id]}
                defaultIntegrations={defaultIntegrationsByCarrier[row.client_id]}
                onOpenContractor={onOpen}
            />
        ),
    });

    if (error) {
        return <div className="px-3 py-2 text-sm">Carriers could not be loaded.</div>;
    }

    return (
        <DataTable
            table={table}
            isLoading={loading}
            emptyState="No carriers yet"
            maxHeight="fill"
            stickyExpandedRows
            className={TABLE_CLASS}
        />
    );
};

export default Carriers;
